<script setup>
import { ref } from 'vue';
import { Head, usePage } from '@inertiajs/vue3';
import { useStorage } from '@vueuse/core';
import { FilterMatchMode } from '@primevue/core/api';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import AuthenticatedAdminLayout from '@/Layouts/AuthenticatedAdminLayout.vue';

const page = usePage();

const toast = useToast();

const reports = ref(
    [ ...page.props.reports ].map(
        report => {
          const creationDate = report.creation_date ? new Date(report.creation_date) : null;
          const updateDate = report.update_date ? new Date(report.update_date) : null;

          return {
            ...report,
            creation_date: creationDate,
            update_date: updateDate,
          };
        }
    )
);

const DEFAULT_FILTERS = {
  'old_order.reference': { value: null, matchMode: FilterMatchMode.CONTAINS },
  'new_order.reference': { value: null, matchMode: FilterMatchMode.CONTAINS },
  reason: { value: null, matchMode: FilterMatchMode.EQUALS },
  status: { value: 'pending', matchMode: FilterMatchMode.EQUALS },
  creation_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
  update_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
};

// The data table component does not have an equivalent for "mergeDefaults", and crashes when new filters are added.
const filters = useStorage(
    'fraud-report-list-filters',
    DEFAULT_FILTERS,
    sessionStorage,
    { mergeDefaults: true }
)

const resetFilters = () => {
  filters.value = { ...DEFAULT_FILTERS };
};

const REASON_LABELS = {
  'same_address': 'Adresse similaire',
  'same_customer': 'Même client',
  'same_phone': 'Même téléphone',
};

const STATUS_LABELS = {
  pending: 'En attente de contrôle',
  ignored: 'Pas de fraude',
  closed: 'Fermé automatiquement',
  confirmed: 'Fraude confirmée',
  processed: 'Fraude traitée',
};

const STATUS_SEVERITIES = {
  closed: 'secondary',
  confirmed: 'danger',
  ignored: 'success',
  pending: 'info',
  processed: 'contrast',
};

const getStatusActions = status => {
  switch (status) {
    case 'pending':
      return [
        {
          label: 'Pas de fraude',
          icon: 'pi pi-check',
          severity: 'success',
          action: 'ignore',
        },
        {
          label: 'Confirmer la fraude',
          icon: 'pi pi-flag',
          severity: 'danger',
          action: 'confirm',
        },
      ];
    case 'confirmed':
      return [
        {
          label: 'Traiter la fraude',
          icon: 'pi pi-folder-open',
          severity: 'contrast',
          action: 'process',
        },
      ];
    default:
      return [];
  }
};

const filteredReports = ref(reports.value);

const onReportListChange = (list) => {
  // Important note: do not copy, keep the same reference, so that status updates can be picked up by the UI.
  filteredReports.value = list;
};

const expandedReportIds = ref([]);

const submittedReportIds = ref(new Set());

const applyActionToReport = (action, report) => {
  if (!submittedReportIds.value.has(report.id) && confirm(`Êtes-vous sûr(e) ?`)) {
    submittedReportIds.value.add(report.id);

    // todo not needed probably (/ and unexisting)
    const payload = {
      _token: page.props.csrf_token,
    }

    axios.post(route(`admin.fraud-report.${action}`, { id: report.id }), payload)
         .then((response) => {
           if (response.data.success) {
             toast.add({
               severity: 'success',
               summary: 'Rapport traité',
               detail: 'Le rapport a bien été mis à jour.',
               life: 5000,
             });

             if (response.data.new_status) {
               for (const [ index, listReport ] of reports.value.entries()) {
                 if (report.id === listReport.id) {
                   reports.value[index].status = response.data.new_status;
                 }
               }

               for (const [ index, listReport ] of filteredReports.value.entries()) {
                 if (report.id === listReport.id) {
                   filteredReports.value[index].status = response.data.new_status;
                 }
               }
             }
           } else {
             throw response;
           }
         })
         .catch((error) => {
           toast.add({
             severity: 'error',
             summary: 'Erreur de traitement',
             detail: error.response.data.error || 'Une erreur est survenue lors du traitement du rapport.',
             life: 5000,
           });
         })
         .finally(() => {
           submittedReportIds.value.delete(report.id);
         });
  }
};

const currencyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

const getFormattedIso8601Date = (iso8601Date, includeTime = false) => {
  const date = new Date(iso8601Date);

  return !includeTime
      ? date.toLocaleDateString()
      : `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const getOrderDetails = (order) => (
    [
      [ 'Référence', order.reference ],
      [ 'Date', getFormattedIso8601Date(order.date) ],
      [ 'Total', currencyFormatter.format(order.total_amount) ],
      [ 'Email', order.email ],
      [ 'Prénom', order.firstname ],
      [ 'Nom', order.lastname ],
      [ 'Adresse', order.address ],
      [ 'Code postal', order.postcode ],
      [ 'Ville', order.city ],
      [ 'Téléphone', order.phone ],
      [
        'Créneau de retrait',
        `${getFormattedIso8601Date(order.pickup_date)} entre ${order.pickup_hour}h et ${order.pickup_hour + 1}h`,
      ],
    ]
);
</script>

<template>
  <AuthenticatedAdminLayout>
    <Head title="Rapports de fraude"/>

    <main class="relative p-4 sm:px-6 lg:px-8 flex flex-col justify-center sm:items-center mx-auto size-full">
      <div class="flex flex-row justify-between items-center pb-4 size-full">
        <h1 class="text-xl sm:text-2xl text-primary uppercase font-semibold">
          Rapports de fraude
        </h1>
      </div>

      <Message severity="info" icon="pi pi-info-circle">
        <div class="px-4 py-2">
          <p>
            Retrouvez ici la liste des fraudes potentielles détectées par notre système,
            parmi les commandes en attente de paiement ou payées.
          </p>
          <p>
            Les rapports qui concernent au moins une commande annulée ou remboursée sont automatiquement fermés
            à intervalles réguliers.
          </p>
        </div>
      </Message>

      <div class="flex flex-row py-4 justify-between size-full">
        <div></div>

        <Button
            label="Réinitialiser les filtres"
            icon="pi pi-filter-slash"
            severity="secondary"
            @click="resetFilters"
        />
      </div>

      <DataTable
          :value="reports"
          dataKey="id"
          v-model:filters="filters"
          v-model:expandedRows="expandedReportIds"
          filterDisplay="row"
          resizableColumns
          columnResizeMode="expand"
          paginator
          :pageLinkSize="10"
          :rows="20"
          :rowsPerPageOptions="[10, 20, 50, 100]"
          removableSort
          scrollable
          scrollHeight="calc(100vh - 15rem)"
          size="large"
          showGridlines
          stripedRows
          style="width: 100%"
          @value-change="onReportListChange"
      >
        <Column expander style="width: 5rem"/>

        <Column field="old_order.reference" header="Ancienne commande" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="new_order.reference" header="Nouvelle commande" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="reason" header="Raison" :showFilterMenu="false" :showClearButton="false" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <Select
                v-model="filterModel.value"
                :options="Object.keys(REASON_LABELS)"
                :optionLabel="option => REASON_LABELS[option]"
                placeholder="Raison"
                @change="filterCallback()"
                :showClear="true"
            >
              <template #option="{ option }">
                {{ REASON_LABELS[option] }}
              </template>
            </Select>
          </template>

          <template #body="{ data }">
            {{ REASON_LABELS[data.reason] }}
          </template>
        </Column>

        <Column field="status" header="Statut" :showFilterMenu="false" :showClearButton="false" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <Select
                v-model="filterModel.value"
                :options="Object.keys(STATUS_LABELS)"
                :optionLabel="option => STATUS_LABELS[option]"
                placeholder="Statut"
                @change="filterCallback()"
                :showClear="true"
            >
              <template #option="{ option }">
                <Tag :value="STATUS_LABELS[option]" :severity="STATUS_SEVERITIES[option]"/>
              </template>
            </Select>
          </template>

          <template #body="{ data }">
            <Tag :value="STATUS_LABELS[data.status]" :severity="STATUS_SEVERITIES[data.status]"/>
          </template>
        </Column>

        <Column field="creation_date" dataType="date" header="Détection le" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
                id="creation_date_filter"
                fluid
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                @update:modelValue="filterCallback"
            />
          </template>

          <template #body="{ data }">
            <span v-if="data.creation_date">
              {{ data.creation_date.toLocaleDateString() }}
              {{ data.creation_date.toLocaleTimeString() }}
            </span>
          </template>
        </Column>

        <Column field="update_date" dataType="date" header="Modification le" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
                id="update_date_filter"
                fluid
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                @update:modelValue="filterCallback"
            />
          </template>

          <template #body="{ data }">
            <span v-if="data.update_date">
              {{ data.update_date.toLocaleDateString() }}
              {{ data.update_date.toLocaleTimeString() }}
            </span>
          </template>
        </Column>

        <Column
            field="actions"
            header="Actions"
            frozen
            alignFrozen="right"
            style="width: 5em !important; max-width: none !important;"
        >
          <template #body="{ data, index }">
            <div class="flex justify-start gap-3">
              <Button
                  v-for="action in getStatusActions(data.status)"
                  :key="action.action"
                  :severity="action.severity"
                  size="small"
                  rounded
                  @click="() => applyActionToReport(action.action, data)"
                  :disabled="submittedReportIds.has(data.id)"
                  :title="action.label"
              >
                <i :class="`pi ${action.icon}`"/>
              </Button>
            </div>
          </template>
        </Column>

        <template #empty>
          <div class="my-4 font-bold text-center">Aucun rapport de fraude trouvé.</div>
        </template>

        <template #expansion="{ data }">
          <div class="p-4">
            <div class="flex flex-row justify-around items-start">
              <div
                  v-for="(order, index) in [ data.old_order, data.new_order ]"
                  :key="index"
                  class="w-full sm:w-1/3 flex flex-col gap-4"
              >
                <h3 class="font-bold text-center">
                  {{ (index === 0) ? 'Ancienne commande' : 'Nouvelle commande' }}
                </h3>

                <table class="size-full">
                  <tbody>
                  <tr
                      v-for="([ label, value ], index) in getOrderDetails(order)"
                      :key="index"
                      :class="{ 'bg-slate-200': (index % 2 === 0) }"
                  >
                    <td class="p-2 font-bold text-left">{{ label }} :</td>
                    <td class="p-2 text-left whitespace-pre	">{{ value }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </DataTable>

      <Toast/>
    </main>
  </AuthenticatedAdminLayout>
</template>
